import { useState, useEffect } from 'react';

import { Card, Col, Row } from 'react-bootstrap';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import { useTranslation } from 'react-i18next';
import RadarChartWidget from './components/RadarChart';
import { Assessment } from './components/DataDefinitions';
import apiClient from 'helpers/apiService';
import Button from 'components/base/Button';
import { useNavigate } from 'react-router-dom';
import career from 'assets/img/th-each-ai/career.png';
import compass from 'assets/img/th-each-ai/compass.jpg';
import job_skills from 'assets/img/th-each-ai/job_skills.png';
import FeedbackMessage from './components/FeedbackMessage';
import PromptWidget from 'components/prompts/PromptWidgets';
import { useMainLayoutContext } from 'providers/MainLayoutProvider';

const MySkills = () => {

    const { t } = useTranslation();
    const navigate = useNavigate();

    const { setFooterClass } = useMainLayoutContext();

    useEffect(() => {
        setFooterClass('d-none');   // hide footer
        return () => {
            setFooterClass('');
        };
    }, []);

    const message: string = `To improve your sales management skills, consider the following strategies:

    Continuous learning:    
        Read books and articles on sales management
        Attend workshops, seminars, and conferences
        Take online courses or pursue relevant certifications
        
    Seek mentorship:    
        Find an experienced sales manager to guide you
        Join professional networks or associations for sales managers
        
    Practice active listening:    
        Improve your communication skills with team members and clients
        Learn to understand and address the needs of your sales team
        
    Develop your coaching skills:    
        Learn how to effectively train and motivate your sales team
        Practice giving constructive feedback
        
    Enhance your analytical skills:
        Learn to interpret sales data and metrics
        Use CRM tools and sales analytics software effectively
        
    Improve time management:    
        Learn to prioritize tasks and delegate effectively
        Use productivity tools and techniques
        
    Stay updated on industry trends:
        Follow industry news and developments
        Understand your market and competition
        
    Work on your leadership skills:    
        Learn different leadership styles and when to apply them
        Practice decision-making and problem-solving
        
    Enhance your emotional intelligence:    
        Develop empathy and self-awareness
        Learn to manage stress and conflicts effectively
    
    Set personal goals:    
        Establish clear, measurable objectives for your skill development
        Regularly review and adjust your progress
        
    Shadow successful sales managers:    
        Observe their techniques and strategies
        Ask for opportunities to assist in their projects
    
    Practice public speaking:    
        Improve your presentation skills
        Join groups like Toastmasters to enhance your communication abilities
        
    Remember, improvement takes time and consistent effort. Regularly assess your progress and adjust your learning strategy as needed."
    `;

    const [assessment, setAssessment] = useState<Assessment>();

    const breadcrumbItems: PageBreadcrumbItem[] = [
        {
            label: t('Dashboard'),
            url: '/dashboard/home'
        },
        {
            label: t('My Skills'),
            active: true
        }
    ];

    const fetchLatestJobSkillsAssessment = async () => {
        try {
            const { data } = await apiClient.get('/v1/skills/assessments/job-skills/latest');
            setAssessment(data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            //            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLatestJobSkillsAssessment();
    }, []);

    return (
        <>
            <PageBreadcrumb items={breadcrumbItems} />

            <div className="mt-4" style={{ height: '100vh', flexDirection: 'column' }}>
                <Row className="mt-3 mb-3">

                    <Card className='border border-light rounded-5 mb-2' >
                        <Card.Body>
                            <div className='align-content-center'>
                                <p className="mb-0 fs-8" style={{ 'textAlign': 'left' }}> {t("MySkills.headline")}</p>
                                <p className="mt-0 fs-8" style={{ 'textAlign': 'left' }}> {t("MySkills.headline2")}</p>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className='border border-light rounded-5' >
                        <Card.Body>
                            <Row className='mt-2'>
                                <Col sm={4} md={4} lg={4}>
                                    <Card onClick={() => navigate("/myskills/job-skills")} className='cursor-pointer'>
                                        <Card.Img variant="top" src={job_skills} />
                                        <Card.Body className='bg-theachai-tiffany'>
                                            <Card.Title className='text-white m-0 p-0 fs-7'>{t("MySkills.job_skill")}</Card.Title>
                                        </Card.Body>
                                    </Card>
                                </Col>

                                <Col sm={4} md={4} lg={4}>
                                    <Card onClick={() => navigate('/myskills/hogan/compass')} className='cursor-pointer'>
                                        <Card.Img variant="top" src={compass} />
                                        <Card.Body className='bg-theachai-yellow'>
                                            <Card.Title className='text-white m-0 p-0 fs-7'>{t("MySkills.compass")}</Card.Title>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col sm={4} md={4} lg={4}>
                                    <Card onClick={() => navigate('/myskills/hogan/career')} className='cursor-pointer'>
                                        <Card.Img variant="top" src={career} />
                                        <Card.Body className='bg-theachai-dark-blue'>
                                            <Card.Title className='text-white m-0 p-0 fs-7'>{t("MySkills.career")}</Card.Title>
                                        </Card.Body>
                                    </Card>
                                </Col>


                            </Row>
                        </Card.Body>
                    </Card>

                </Row>

                {/* Second row */}
                <Row className="mt-4 flex-grow-1" style={{ height: '50%' }}>
                    <Col lg={4} xl={4} className="d-none d-lg-block h-100">
                        <Card className="h-100 w-100">
                            <Card.Header className="d-flex hover-text-100 fs-7 fw-semi-bold align-items-center">
                                <h4 className="flex-grow-1">{t("Your last job skills assessment")}</h4>
                                <Button variant="primary">
                                    {t("MySkills.get_report")}
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                <RadarChartWidget data={assessment} />
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={8} xl={8} className="d-none d-lg-block h-100">
                        <Card className="h-100 w-100 d-flex flex-column">
                            <Card.Body className="flex-grow-1 overflow-auto">
                                <div style={{ minHeight: 0 }}>
                                    <FeedbackMessage message={message} />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>


                <Row className="mt-3 mb-3">
                    <PromptWidget page="skills" />
                </Row>
            </div>

        </>
    );
};

export default MySkills;
